/**
 * In-app documentation registry.
 *
 * Every ``content/<slug>.md`` and ``content/roles/<slug>.md`` is eagerly loaded
 * as a raw string via Vite's ``import.meta.glob`` so DocsPage can render it with
 * the MarkdownRenderer. To add a new doc: drop a Markdown file in ``content/``
 * (or ``content/roles/``) and register its slug in the relevant group below.
 */

// Raw Markdown bodies, keyed by slug (filename without extension).
const sectionModules = import.meta.glob('./content/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
})
const roleModules = import.meta.glob('./content/roles/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
})

function toSlug(path) {
  return path.split('/').pop().replace(/\.md$/, '')
}

// slug -> markdown body
export const DOCS_BY_SLUG = {}
for (const [path, body] of Object.entries(sectionModules)) {
  DOCS_BY_SLUG[toSlug(path)] = body
}
for (const [path, body] of Object.entries(roleModules)) {
  // role guides live under a "roles/<slug>" namespace to avoid collisions
  DOCS_BY_SLUG[`roles/${toSlug(path)}`] = body
}

/**
 * Sidebar grouping. ``slug`` maps into DOCS_BY_SLUG; ``title`` is the nav label.
 */
export const DOC_GROUPS = [
  {
    group: 'Getting Started',
    items: [{ slug: 'getting-started', title: 'Getting Started' }],
  },
  {
    group: 'Section Guides',
    items: [
      { slug: 'dashboard', title: 'Dashboard' },
      { slug: 'assets', title: 'Assets & Equipment' },
      { slug: 'work-orders', title: 'Work Orders' },
      { slug: 'maintenance', title: 'Preventive Maintenance' },
      { slug: 'inventory', title: 'Spare Parts & Inventory' },
      { slug: 'notifications', title: 'Notifications' },
      { slug: 'reports', title: 'Reports & Exports' },
      { slug: 'administration', title: 'Administration' },
      { slug: 'troubleshooting', title: 'Troubleshooting' },
    ],
  },
  {
    group: 'Role Guides',
    items: [
      { slug: 'roles/administrator', title: 'Administrator' },
      { slug: 'roles/biomedical-engineer', title: 'Biomedical Engineer' },
      { slug: 'roles/technician', title: 'Technician' },
      { slug: 'roles/department-head', title: 'Department Head' },
      { slug: 'roles/it-support', title: 'IT Support' },
      { slug: 'roles/storekeeper', title: 'Storekeeper' },
      { slug: 'roles/procurement-officer', title: 'Procurement Officer' },
      {
        slug: 'roles/radiation-oncology-physicist',
        title: 'Radiation Oncology Physicist',
      },
      { slug: 'roles/general-staff', title: 'General Staff' },
    ],
  },
]

export const DEFAULT_DOC_SLUG = 'getting-started'

/**
 * Maps an RBAC role ``name`` (lowercased, from ``user.profile.roles[].name``)
 * to its role guide slug, powering the "Your role" shortcut on the Help landing
 * page. The Role model has no codename, so we key by name with a few aliases.
 */
export const ROLE_NAME_TO_SLUG = {
  administrator: 'roles/administrator',
  admin: 'roles/administrator',
  'biomedical engineer': 'roles/biomedical-engineer',
  'biomedical technician': 'roles/biomedical-engineer',
  technician: 'roles/technician',
  'department head': 'roles/department-head',
  'department head / manager': 'roles/department-head',
  'it support': 'roles/it-support',
  storekeeper: 'roles/storekeeper',
  'store keeper': 'roles/storekeeper',
  'procurement officer': 'roles/procurement-officer',
  'radiation oncology physicist': 'roles/radiation-oncology-physicist',
  'medical physicist': 'roles/radiation-oncology-physicist',
  'general staff': 'roles/general-staff',
}

/**
 * Resolves a role guide slug from a role name, falling back to a slugified
 * ``roles/<name>`` when there is no explicit mapping.
 */
export function roleSlugForName(name) {
  if (!name) return null
  const key = name.trim().toLowerCase()
  if (ROLE_NAME_TO_SLUG[key]) return ROLE_NAME_TO_SLUG[key]
  const slug = `roles/${key.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`
  return DOCS_BY_SLUG[slug] ? slug : null
}

// Flat slug -> title lookup for headings/mobile picker.
export const TITLE_BY_SLUG = {}
for (const { items } of DOC_GROUPS) {
  for (const { slug, title } of items) TITLE_BY_SLUG[slug] = title
}
